/**
 * DealMenu — the hero's ⋯ menu (deal detail slice 1, §39.9 R7 + Rider A):
 * Edit, and Delete ONLY because `DELETE /v1/sales/deals/{id}` already exists
 * (server.go, requireOwnerOrAdminDestructive — a hard delete). Duplicate is
 * not built (no route; its own register row).
 *
 * The Delete item is rendered only when the session's /v1/me role is one the
 * server's destructive gate allows (owner or admin — Dashboard's fail-closed
 * `canDecide`); the server's 403 stays the backstop and its sentence shows if
 * it ever fires (§32 S-8: the toast shows the wire). Confirming names the
 * deal and says the delete is permanent.
 *
 * The menu is the board card's kebab pattern (SalesPipeline DealCard): local
 * state, a click-away backdrop, Escape closes; roles menu / menuitem for the
 * reader. The confirmation is an aria-modal alertdialog, so the single-key
 * guard (keyboardGuard.isModalOpen) parks J / K and L while it is open.
 */
import { useEffect, useRef, useState } from 'react'
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { danger, ink, overlay } from '../../../../theme/tokens'

export const DELETE_PERMANENT_COPY = 'This permanently deletes the deal. It cannot be undone.'

export function DeleteDealDialog({ dealTitle, deleting, onCancel, onConfirm }: {
  dealTitle: string
  deleting: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const cancelRef = useRef<HTMLButtonElement>(null)
  useEffect(() => { cancelRef.current?.focus() }, [])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onCancel() } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onCancel])
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: overlay.scrim }}>
      <div role="alertdialog" aria-modal="true" aria-labelledby="delete-deal-title" aria-describedby="delete-deal-body"
        className="k-card w-full max-w-md p-6 flex flex-col gap-4">
        <h2 id="delete-deal-title" className="text-16 font-semibold text-ink-primary">Delete {dealTitle}?</h2>
        <p id="delete-deal-body" className="text-13 text-ink-secondary">{DELETE_PERMANENT_COPY}</p>
        <div className="flex items-center justify-end gap-2">
          <button ref={cancelRef} type="button" className="k-btn-secondary" disabled={deleting} onClick={onCancel}>Cancel</button>
          <button type="button" className="k-btn disabled:opacity-60" disabled={deleting}
            style={{ background: danger.DEFAULT, color: ink['on-accent'] }} onClick={onConfirm}>
            {deleting ? 'Deleting…' : 'Delete deal'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function DealMenu({ dealTitle, canDelete, deleting = false, onEdit, onDelete }: {
  dealTitle: string
  /** The session role is one the server's destructive gate allows. Fail closed. */
  canDelete: boolean
  deleting?: boolean
  onEdit: () => void
  onDelete: () => void
}) {
  const [open, setOpen] = useState(false)
  const [confirming, setConfirming] = useState(false)
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); setOpen(false) } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])
  return (
    <div className="relative">
      <button type="button" className="k-btn-secondary k-btn-secondary--flex" aria-label="Deal actions"
        aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <MoreHorizontal size={14} aria-hidden="true" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div role="menu" aria-label="Deal actions" className="k-menu k-menu--below"
            style={{ left: 'auto', right: 0, minWidth: 180, zIndex: 20 }}>
            <button type="button" role="menuitem" className="k-menuitem" onClick={() => { setOpen(false); onEdit() }}>
              <Pencil size={14} aria-hidden="true" /> Edit
            </button>
            {canDelete && (
              <button type="button" role="menuitem" className="k-menuitem k-menuitem--danger"
                onClick={() => { setOpen(false); setConfirming(true) }}>
                <Trash2 size={14} aria-hidden="true" /> Delete…
              </button>
            )}
          </div>
        </>
      )}
      {confirming && (
        <DeleteDealDialog dealTitle={dealTitle} deleting={deleting}
          onCancel={() => setConfirming(false)}
          onConfirm={() => { onDelete() }} />
      )}
    </div>
  )
}
